import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./friend-payment-request-details-page.module.scss";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { Transaction, TRANSACTION_STATUS, TransactionStatusMap } from "../../../../../model/transaction";
import { formatDate, formatTime } from "../../history.utils";
import { formatCurrency, handleCopy } from "../../../../../utils";
import { LinkChainIcon } from "../../../../../assets/icons";

const ClockIcon = () => (
	<svg width="14" height="14" viewBox="0 0 18 18" fill="none">
		<path
			d="M9 4.5V9L12 11M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface FriendPaymentRequestDetailsPageProps {
	transaction: Transaction;
	onBack: () => void;
}

const formatDateTime = (value: string) => `${formatDate(value)}, ${formatTime(value).toLocaleLowerCase()}`;

export const FriendPaymentRequestDetailsPage = ({ transaction, onBack }: FriendPaymentRequestDetailsPageProps) => {
	const { t } = useTranslation();
	const friendRequest = transaction.friendPaymentRequest;
	const meterNumber = transaction.vendingDetails?.meterNumber ?? transaction.billingDetails?.meterNumber ?? "";
	const meterAddress = transaction.vendingDetails?.address ?? transaction.billingDetails?.address ?? "";

	const isPending = transaction.status === TRANSACTION_STATUS.IN_PROGRESS;
	const isCompleted = transaction.status === TRANSACTION_STATUS.COMPLETED;
	const statusLabel = isPending ? t("common.labels.pending") : TransactionStatusMap[transaction.status];
	const statusStepColor = isCompleted ? styles.stepIconSuccess : isPending ? styles.stepIconPending : styles.stepIconFailed;

	const handleShareLink = async () => {
		if (!friendRequest?.requestLink) return;
		handleCopy(friendRequest.requestLink);
		try {
			const navAny: any = navigator;
			if (typeof navAny.share === "function") {
				await navAny.share({ url: friendRequest.requestLink });
			}
		} catch {
			// user cancelled the native share sheet — link is already copied
		}
	};

	return (
		<CustomerPageLayout title={t("history.requestDetails")} onBack={onBack}>
			<div className={styles.pageContainer}>
				<div className={styles.detailsCard}>
					<div className={styles.row}>
						<span className={styles.label}>{t("history.recipient")}</span>
						<span className={styles.value}>{friendRequest?.recipientEmail}</span>
					</div>
					<div className={styles.row}>
						<span className={styles.label}>{t("common.labels.meterNumber")}</span>
						<span className={styles.value}>{meterNumber}</span>
					</div>
					{meterAddress && (
						<div className={styles.row}>
							<span className={styles.label}>{t("common.labels.meterAddress")}</span>
							<span className={styles.value}>{meterAddress}</span>
						</div>
					)}
					<div className={styles.row}>
						<span className={styles.label}>{t("history.requestType")}</span>
						<span className={styles.value}>{t("history.requestForAFriend")}</span>
					</div>
					<div className={styles.row}>
						<span className={styles.label}>{t("common.labels.amount")}</span>
						<span className={styles.value}>{formatCurrency(transaction.amount.amount, { currency: transaction.amount.currency })}</span>
					</div>
				</div>

				<CardFrame headerSectionTitle={t("history.requestJourney")}>
					<div className={styles.journey}>
						<div className={styles.step}>
							<div className={styles.stepMarker}>
								<div className={`${styles.stepIcon} ${styles.stepIconSuccess}`}>✓</div>
								<div className={styles.stepLine} />
							</div>
							<div className={styles.stepContent}>
								<span className={styles.stepTitle}>{t("history.requestInitiated")}</span>
								<span className={styles.stepDate}>{formatDateTime(transaction.createdAt)}</span>
							</div>
						</div>
						<div className={styles.step}>
							<div className={styles.stepMarker}>
								<div className={`${styles.stepIcon} ${styles.stepIconSuccess}`}>✓</div>
								<div className={styles.stepLine} />
							</div>
							<div className={styles.stepContent}>
								<span className={styles.stepTitle}>{t("history.emailSmsSentToFriend")}</span>
								<span className={styles.stepDate}>{formatDateTime(transaction.createdAt)}</span>
							</div>
						</div>
						<div className={styles.step}>
							<div className={styles.stepMarker}>
								<div className={`${styles.stepIcon} ${statusStepColor}`}>
									{isPending ? <ClockIcon /> : isCompleted ? "✓" : "✕"}
								</div>
							</div>
							<div className={styles.stepContent}>
								<span className={styles.stepTitle}>{t("history.transactionStatusStep", { status: statusLabel })}</span>
								{!isPending && <span className={styles.stepDate}>{formatDateTime(transaction.updatedAt as string)}</span>}
							</div>
						</div>
					</div>
				</CardFrame>

				{friendRequest?.requestLink && (
					<button type="button" className={styles.shareButton} onClick={() => handleShareLink()}>
						<LinkChainIcon />
						{t("history.shareRequestLinkAgain")}
					</button>
				)}
			</div>
		</CustomerPageLayout>
	);
};
